import { engineComponent, NumberUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { Symbols } from "../GameComponents/Symbols";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../GameComponents/Base/BaseView";
import { Paylines } from "../GameComponents/Paylines";
import { Symbol } from "../GameComponents/Symbol";
import { Communication } from "neo-games";
import { Settings } from "./Settings";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { Languages } from "../Utils/Languages";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { PlayList } from "../Components/Sounds/PlayList";
import { Tweens } from "../Components/Effects/Tweens";
import { ImageTextCounterElementExtended } from "./ImageTextCounterElementExtended";
import { Payline } from "../GameComponents/Payline";
import { TimelineElementKDG } from "./TimelineElementKDG";
import { SymbolExtended } from "./SymbolExtended";
import { ReelExtended } from "./ReelExtended";

@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimatorExtended extends WinAnimator {
    private _paylines: Paylines;
    private _cycleAnimationName: string = "Value";

    private _paylineWinDelay: number = 60; /* Delay between win lines */
    private _paylineWinEndDelay: number = 90; /* Delay after win lines, before cycle */
    private _paylineCycleDelay: number = 90; /* Delay between cycle lines */

    private _highWinsAnimationsMystery: Array<string>;
    private _highWinsMystery: Array<TimelineElementKDG>;
    private _currentMystery: TimelineElementKDG;
    private _mysteryStarted: Boolean = false;
    private _mysteryFinished: Boolean = false;

    private _activeElementsNames: Array<string> = [];
    private _activeElements: Array<SwitchElement> = [];
    private _activeElementsCase: string;

    protected static readonly CaseNormal = "NORMAL";
    protected static readonly CaseWin = "WIN";
    protected static readonly CaseNoWin = "NOWIN";
    protected static readonly CaseHighWin = "HIGH";
    protected static readonly CaseMystery = "MYSTERY";
    protected static readonly CaseMysteryCycle = "MYSTERY_CYCLE";
    protected static readonly CaseWinOut = "WIN_OUT";
    protected static readonly CaseHighCycle = "HIGH_CYCLE";
    protected static readonly CaseCycle = "WIN_CYCLE";
    protected static readonly CaseTease = "TEASE";

    protected counter: ImageTextCounterElementExtended;
    protected currentCounterStep: number;

    protected static readonly ShowFirstWinsStep = "showFirstWinsStep";
    protected static readonly EndFirstWinsStep = "endFirstWinsStep";
    protected static readonly FinishWin = "finishWin";

    configure(node: Element): void {
        super.configure(node);

        if (node.hasAttribute(WinAnimatorExtendedAttributes.HIGH_WINS_MYSTERY_ANIMATIONS)) {
            this._highWinsAnimationsMystery = node
                .getAttribute(WinAnimatorExtendedAttributes.HIGH_WINS_MYSTERY_ANIMATIONS)
                .split(";");
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS)) {
            this._activeElementsNames = node.getAttribute(WinAnimatorExtendedAttributes.ACTIVE_ELEMENTS).split(";");
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION)) {
            this._cycleAnimationName = node.getAttribute(WinAnimatorExtendedAttributes.CYCLE_ANIMATION);
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY)) {
            this._paylineWinDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_END_DELAY)) {
            this._paylineWinEndDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_WIN_END_DELAY));
        }

        if (node.hasAttribute(WinAnimatorExtendedAttributes.PAYLINE_CYCLE_DELAY)) {
            this._paylineCycleDelay = parseInt(node.getAttribute(WinAnimatorExtendedAttributes.PAYLINE_CYCLE_DELAY));
        }
    }

    initialize(
        symbols: Symbols,
        paylines?: Paylines,
        noWinSymbolsContainer?: ContainerElement,
        symbolsContainer?: ContainerElement,
        parent?: BaseView,
    ): void {
        this._paylines = paylines;

        super.initialize(symbols, paylines, noWinSymbolsContainer, symbolsContainer);

        if (!this.value && this._cycleAnimationName && this.childs[this._cycleAnimationName]) {
            this.value = this.childs[this._cycleAnimationName];
            this.animationsContainer.addChild(this.value);
        }

        if (parent) {
            this._activeElementsNames.forEach((elementName) => {
                if (parent.childs[elementName] && parent.childs[elementName] instanceof SwitchElement) {
                    this._activeElements.push(parent.childs[elementName]);
                }
            });
        }

        this.createCounter();
    }

    protected createAnimations() {
        super.createAnimations();

        if (this._highWinsAnimationsMystery != null && this._highWinsAnimationsMystery.length > 0) {
            this._highWinsMystery = [];
            for (let j: number = 0; j < this._highWinsAnimationsMystery.length; j++) {
                if (this.childs[this._highWinsAnimationsMystery[j]] != null) {
                    this.childs[this._highWinsAnimationsMystery[j]].on("finished", this.onAnimationFinished.bind(this));
                    this.childs[this._highWinsAnimationsMystery[j]].on(
                        TimelineElement.AnimationComplete,
                        this.onAnimationComplete.bind(this),
                    );
                    this._highWinsMystery.push(this.childs[this._highWinsAnimationsMystery[j]]);
                } else {
                    if (DEBUG) {
                        this.log("ERROR no mystery high wins with name " + this._highWinsAnimationsMystery[j]);
                    }
                }
            }
        }
    }

    protected onAnimationFinished() {
        this._mysteryFinished = true;
        if (this._mysteryStarted) {
            PlayList.loop("SND_CycleLoop", 120);
        }
        super.onAnimationFinished();
    }

    protected onAnimationComplete() {
        this._mysteryFinished = true;
        super.onAnimationComplete();
    }

    //******************************************************************************************************************
    //  SYMBOLS
    //******************************************************************************************************************

    /**
     * Sorts symbols according to their zIndex attribute, if applicable
     */
    private sortSymbols(): void {
        if (!this.symbols.hasZindex) {
            return;
        }
        let addQueue: Symbol[] = [];
        this.symbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.symbolsContainer.addChild(symbol);
        });
        addQueue = [];
        this.noWinSymbolsContainer.children.forEach((symbol) => {
            addQueue.push(<Symbol>symbol);
        });
        addQueue.sort((a, b) => a.zIndex - b.zIndex);
        addQueue.forEach((symbol) => {
            this.noWinSymbolsContainer.addChild(symbol);
        });
    }

    showSymbols(reelsData: Array<Communication.Game.IReel>) {
        super.showSymbols(reelsData);
        this.sortSymbols();
    }

    protected teasingSymbolCount(reelIndex: number): number {
        let count = 0;

        for (let i = 0; i <= reelIndex; i++) {
            for (let j = 0; j < this.currentSymbols[i].length; j++) {
                if (this.currentSymbols[i][j] && this.currentSymbols[i][j].id === Settings.SymbolTeasing - 1) {
                    count++;
                }
            }
        }

        return count;
    }

    onReelStopped(reelsData: Array<Communication.Game.IReel>, reelIndex: number) {
        super.onReelStopped(reelsData, reelIndex);
        let totalDice = this.teasingSymbolCount(reelIndex);
        if (this.reelCount - 1 - reelIndex + totalDice >= 3) {
            for (let i = 0; i < this.currentSymbols[reelIndex].length; i++) {
                const symbol = this.currentSymbols[reelIndex][i];
                if (symbol !== null && symbol.id === Settings.SymbolTeasing - 1) {
                    symbol.goToCase(Symbol.CasePulse);
                    totalDice++;
                }
            }
        }
        if (reelIndex < this.reelCount - 1 && this.teasingSymbolCount(reelIndex) == 2) {
            this.setActiveElementCaseArray([WinAnimatorExtended.CaseTease]);
        }

        if (reelIndex < this.reelCount - 1 && this.teasingSymbolCount(reelIndex) < 3) {
            this.setActiveElementCaseArray([WinAnimatorExtended.CaseNormal]);
        }

        if (ReelExtended.Tease) {
            this.currentSymbols.forEach((reel) => {
                reel.forEach((symbol) => {
                    if (symbol && symbol.id !== Settings.SymbolTeasing - 1) {
                        if (reelIndex == this.reelCount - 1 && totalDice < 3) {
                            symbol.goToCase(Symbol.CaseNormal);
                        } else {
                            symbol.goToCase(Symbol.CaseNowin);
                        }
                    }
                });
            });
        }

        this.sortSymbols();
    }

    protected allSymbolsGoToReset() {
        super.allSymbolsGoToReset();
        this.sortSymbols();
    }

    protected allSymbolsGoToCase(caseType: string, noWin: boolean) {
        super.allSymbolsGoToCase(caseType, noWin);
        this.sortSymbols();
    }

    protected winSymbolsGoToCase(winIndex: number, caseType: string, useValue: boolean): Array<Symbol> {
        const win = this.currentWins[winIndex];
        let symbols: Array<Symbol> = [];

        switch (win.trigger) {
            case Communication.Game.WinTrigger.Payline:
                symbols = this.winSymbolsGoToCaseAsPayline(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Scatter:
                symbols = this.winSymbolsGoToCaseAsScatter(winIndex, caseType);
                break;
            case Communication.Game.WinTrigger.Way:
                symbols = this.winSymbolsGoToCaseAsWay(winIndex, caseType);
                break;
            default:
                symbols = this.winSymbolsGoToCaseAsCustom(winIndex, caseType);
                break;
        }

        if (useValue && symbols.length > 0 && win.amount > 0 && !this._mysteryStarted) {
            this.value.visible = true;
            this.value.position.x = symbols[0].position.x + symbols[0].offsetX + this.symbolWidth / 2;
            this.value.position.y = symbols[0].position.y + symbols[0].offsetY + this.symbolHeight / 2;

            if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                ImageTextCounterElement.Round = true;
                this.value.play(parseInt(NumberUtils.devide(win.amount, BetManager.CoinSize).toString()));
            } else {
                ImageTextCounterElement.Round = false;
                this.value.play(parseFloat(Languages.formatText(Languages.FormatMoney, win.amount.toString())));
            }
        } else {
            this.value.visible = false;
        }

        this.sortSymbols();
        return symbols;
    }

    public getAnimationIndex(): number {
        if (this.currentWins.length === 1 && this.currentWins[0].trigger == Communication.Game.WinTrigger.Scatter) {
            return 5; // Empty animation
        }

        return super.getAnimationIndex();
    }

    //******************************************************************************************************************
    //  WINS
    //******************************************************************************************************************

    showWins(wins: Array<Communication.Game.IWin>, win: number): boolean {
        let ret = super.showWins(wins, win);

        if (win > 0) {
            PlayList.stop("SND_BaseLoop");
            this._paylines.visible = true;

            if (this.currentMultiplierIndex > 1) {
                // priority: HIGH + number -> HIGH -> WIN
                this.setActiveElementCaseArray([
                    WinAnimatorExtended.CaseHighWin + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseHighWin,
                    WinAnimatorExtended.CaseWin,
                ]);
            } else {
                // priority: WIN
                if (this._mysteryStarted) {
                    this.setActiveElementCaseArray([WinAnimatorExtended.CaseMystery, WinAnimatorExtended.CaseWin]);
                } else {
                    this.setActiveElementCaseArray([WinAnimatorExtended.CaseWin]);
                }
            }
        } else {
            // priority: NOWIN
            this.setActiveElementCaseArray([WinAnimatorExtended.CaseNoWin]);
        }

        return ret;
    }

    protected showFirstWins(): void {
        if (this.currentMultiplierIndex === 0) {
            this.currentCounterStep = 0;

            this.startTimer(WinAnimatorExtended.ShowFirstWinsStep, 15);
        } else {
            super.showFirstWins();
        }
    }

    protected showFirstWinsStep(): void {
        const win = this.currentWins[this.currentCounterStep];

        this.allPaylinesGoToCase(Payline.CaseNowin);
        this.allSymbolsGoToCase(Symbol.CaseNowin, true);

        for (let i = 0; i < this.currentWins.length; i++) {
            this.winPaylineGoToCase(i, Payline.CaseNormal);
            this.winSymbolsGoToCase(i, Symbol.CaseNormal, false);
        }

        this.winPaylineGoToCase(this.currentCounterStep, Payline.CaseWin);
        const symbols = this.winSymbolsGoToCase(this.currentCounterStep, Symbol.CaseWin, false);

        this.runCounterStep();

        this.currentCounterStep++;

        if (this.currentWin / BetManager.Bet > 1) {
            this.startTimer(
                this.currentCounterStep < this.currentWins.length
                    ? WinAnimatorExtended.ShowFirstWinsStep
                    : WinAnimatorExtended.FinishWin,
                this._paylineWinDelay,
            );
        } else {
            this.startTimer(
                this.currentCounterStep < this.currentWins.length
                    ? WinAnimatorExtended.ShowFirstWinsStep
                    : WinAnimatorExtended.FinishWin,
                0,
            );
        }
    }

    protected finishWin(): void {
        this.emit(WinAnimator.FinishWinCalculation);

        this.runCounterOutro();

        this.startTimer(WinAnimatorExtended.EndFirstWinsStep, this._paylineWinEndDelay);
    }

    private setActiveCases(): void {
        // out of normal wins
        if (this.currentMultiplierIndex > 1) {
            if (this._mysteryStarted) {
                this.setActiveElementCaseArray([
                    WinAnimatorExtended.CaseMysteryCycle,
                    WinAnimatorExtended.CaseHighCycle + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseHighCycle,
                    WinAnimatorExtended.CaseCycle,
                ]);
            } else {
                this.setActiveElementCaseArray([
                    WinAnimatorExtended.CaseHighCycle + this.currentMultiplierIndex,
                    WinAnimatorExtended.CaseHighCycle,
                    WinAnimatorExtended.CaseCycle,
                ]);
            }
        } else {
            // priority: WIN
            if (this._mysteryStarted) {
                this.setActiveElementCaseArray([WinAnimatorExtended.CaseMysteryCycle, WinAnimatorExtended.CaseCycle]);
            } else {
                this.setActiveElementCaseArray([WinAnimatorExtended.CaseCycle]);
            }
        }
    }

    protected finishFirstWins(): void {
        PlayList.loop("SND_CycleLoop", 30);
        this.setActiveCases();
        if (this._mysteryStarted) {
            // no cycle for mystery
            this.emit(WinAnimator.FinishWinAnimator);
            this.currentCycle = 0;
        } else {
            super.finishFirstWins();
        }
    }

    protected showCustomInitWin() {
        let i = 0;

        this.currentMultiplierIndex = 0;
        for (let i = 0; i < this.multipliers.length; i++) {
            if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                this.currentMultiplierIndex = i;
            }
        }

        for (let win of this.currentWins) {
            if (win.trigger == Communication.Game.WinTrigger.Scatter) {
                /*this._mysteryStarted = true;
                this._mysteryFinished = false;*/

                this.allSymbolsGoToCase(WinAnimatorExtended.CaseNoWin, true);
                this.winSymbolsGoToCase(i, WinAnimatorExtended.CaseNormal, true);

                /*
                this._currentMystery = this._highWinsMystery[this.currentMultiplierIndex];
                this._highWinsMystery[this.currentMultiplierIndex].visible = true;
                if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                    ImageTextCounterElement.Round = true;
                    this._highWinsMystery[this.currentMultiplierIndex].goToOutro(parseInt((NumberUtils.devide(this.currentWin, BetManager.CoinSize)).toString()), true);
                } else {
                    ImageTextCounterElement.Round = false;
                    this._highWinsMystery[this.currentMultiplierIndex].goToOutro(this.currentWin, true);
                }*/
            }
            i++;
        }
    }

    protected allSymbolsGoToCaseMystery(caseType: string, noWin: boolean): void {
        for (let i = 0; i < this.currentSymbols.length; i++) {
            for (let j = this.currentSymbols[i].length - 1; j >= 0; j--) {
                const symbol = this.currentSymbols[i][j];

                if (symbol !== null && this.allowSymbolGoToCase(i, j, caseType)) {
                    if (symbol.id != Settings.SymbolTeasing - 1 && symbol.getCurrentCase() != "PULSE") {
                        symbol.goToCase(caseType);
                    }

                    if (noWin) {
                        this.noWinSymbolsContainer.addChild(symbol);
                    } else {
                        this.symbolsContainer.addChild(symbol);
                    }
                }
            }
        }
    }

    protected showCustomWin(): boolean {
        let i = 0;
        for (let win of this.currentWins) {
            if (win.trigger == Communication.Game.WinTrigger.Scatter) {
                this._mysteryStarted = true;
                this._mysteryFinished = false;

                // this.allSymbolsGoToCase(WinAnimatorExtended.CaseNoWin, true);
                this.allSymbolsGoToCaseMystery(WinAnimatorExtended.CaseNoWin, true);
                this.winSymbolsGoToCase(i, WinAnimatorExtended.CaseNormal, true);

                this._currentMystery = this._highWinsMystery[this.currentMultiplierIndex];
                this._highWinsMystery[this.currentMultiplierIndex].visible = true;
                if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
                    ImageTextCounterElement.Round = true;
                    this._highWinsMystery[this.currentMultiplierIndex].play(
                        parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()),
                    );
                } else {
                    ImageTextCounterElement.Round = false;
                    this._highWinsMystery[this.currentMultiplierIndex].play(this.currentWin);
                }

                return true;
            }
            i++;
        }

        return false;
    }

    onTimerComplete(): void {
        this.timer.stop();

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.showFirstWinsStep();
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.setActiveCases();
                this.emit(WinAnimator.FinishWinAnimator);
                this.currentCycle = 0;
                this.cycleWins(true);
                break;
            case WinAnimatorExtended.FinishWin:
                this.finishWin();
                break;
            default:
                super.onTimerComplete();
                break;
        }
    }

    protected winSymbolsGoToCaseAsScatter(winIndex: number, caseType: string): Array<Symbol> {
        const win = this.currentWins[winIndex];
        const symbols: Array<Symbol> = [];

        for (let i = 0; i < this.reelCount; i++) {
            for (let j = 0; j < this.symbolCount; j++) {
                if ((win.mask[i] & (1 << j)) !== 0 && this.allowSymbolGoToCase(i, j, caseType)) {
                    const symbol = this.currentSymbols[i][j];
                    if (symbol.getCurrentCase() != "PULSE") {
                        symbol.goToCase(caseType);
                    }

                    this.symbolsContainer.addChild(symbol);

                    symbols.push(symbol);
                }
            }
        }

        return symbols;
    }

    //******************************************************************************************************************
    //  COUNTER
    //******************************************************************************************************************

    protected createCounter(): void {
        this.counter = this.childs["Counter"];

        this.animationsContainer.addChild(this.counter);
    }

    protected resetCounter(): void {
        Tweens.removeTweens(this.counter, "all");

        this.counter.alpha = 0;
    }

    private getCounterStep(): number {
        return (0.8 - 0.5) / this.currentWins.length;
    }

    protected runCounterStep(): void {
        let glow = this.childs["CounterGlow"];
        glow.visible = true;
        glow.scale.x = 2;
        glow.scale.y = 1;
        if (this.currentCounterStep == 0) {
            Tweens.alphaTween(glow, {
                alphas: [0, 1],
                tweenDuration: 5,
            });
        } else {
            glow.alpha = 1;
        }

        PlayList.play("SND_BasePayline" + Math.min(this.currentCounterStep + 1, 16));

        // let overlay = this._background.getChild("ColorOverlay");
        // Tweens.removeTweens(overlay, 'all');
        // Tweens.alphaTween(overlay, {
        //     alphas: [0.5, 0, 0.5],
        //     tweenDuration: 15
        // });
        //
        //
        let amountFrom = "0";
        let amountTo = "0";

        for (let i = 0; i <= this.currentCounterStep; i++) {
            const win = this.currentWins[i];

            if (win.type === Communication.Game.WinType.Cash) {
                if (i < this.currentCounterStep) {
                    amountFrom = NumberUtils.add(amountFrom, this.currentWins[i].amount);
                }

                amountTo = NumberUtils.add(amountTo, this.currentWins[i].amount);
            }
        }

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.counter.start(
                parseInt(NumberUtils.devide(amountFrom, BetManager.CoinSize).toString()),
                parseInt(NumberUtils.devide(amountTo, BetManager.CoinSize).toString()),
                0,
            );
        } else {
            ImageTextCounterElement.Round = false;
            this.counter.start(parseFloat(amountFrom), parseFloat(amountTo), 0);
        }

        const step = this.getCounterStep();

        Tweens.removeTweens(this.counter, "all");
        Tweens.alphaTween(this.counter, {
            alphas: [this.counter.alpha, 1],
            tweenDuration: 20,
        });

        Tweens.scaleTween(this.counter, {
            tweenValues: [
                new PIXI.Point(0.5 + step * this.currentCounterStep, 0.5 + step * this.currentCounterStep),
                new PIXI.Point(
                    0.55 + step * (this.currentCounterStep + 1),
                    0.55 + step * (this.currentCounterStep + 1),
                ),
                new PIXI.Point(
                    0.55 + step * (this.currentCounterStep + 1),
                    0.55 + step * (this.currentCounterStep + 1),
                ),
                new PIXI.Point(
                    0.55 + step * (this.currentCounterStep + 1),
                    0.55 + step * (this.currentCounterStep + 1),
                ),
                new PIXI.Point(0.5 + step * (this.currentCounterStep + 1), 0.5 + step * (this.currentCounterStep + 1)),
            ],
            tweenDuration: 55,
            tweenEase: "backInOut",
        });
    }

    protected runCounterOutro(reset: boolean = false): void {
        if (this.currentMultiplierIndex > 0) {
            return;
        }
        let glow = this.childs["CounterGlow"];
        this.counter.glowAsWin();

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            this.counter.start(0, parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()), 0);
        } else {
            ImageTextCounterElement.Round = false;
            this.counter.start(0, this.currentWin, 0);
        }

        let counterSize = 0.5 + this.getCounterStep() * (reset ? this.currentWins.length : this.currentCounterStep);

        glow.visible = true;
        Tweens.removeTweens(glow, "all");
        Tweens.alphaTween(glow, {
            alphas: [glow.alpha, 1],
            tweenDuration: 5,
            tweenDelay: 0,
            callbackFunction: (params) => {
                PlayList.play("SND_BaseWinFinal");
            },
            callbackType: "onStart",
        });
        Tweens.scaleTween(glow, {
            tweenValues: [new PIXI.Point(2, 1), new PIXI.Point(2.2, 1.2), new PIXI.Point(2, 1)],
            tweenDuration: 15,
            tweenDelay: 5,
        });

        let finish = (reset: boolean = false) => {
            Tweens.scaleTween(glow, {
                tweenValues: [new PIXI.Point(2, 1), new PIXI.Point(3, 2)],
                tweenDuration: 20,
                tweenDelay: 20,
            });
            Tweens.alphaTween(glow, {
                alphas: [1, 0],
                tweenDuration: 20,
                tweenDelay: 20,
                callbackFunction: (params) => {
                    glow.visible = false;
                },
            });

            Tweens.alphaTween(this.counter, {
                alphas: [1, 0],
                tweenDuration: 20,
                tweenDelay: 20,
                callbackFunction: (params) => {
                    if (reset) {
                        PlayList.stop("SND_BaseWinFinal");
                    }
                    PlayList.play("SND_BaseWinAway");
                },
                callbackType: "onStart",
            });
            Tweens.scaleTween(this.counter, {
                tweenValues: [
                    new PIXI.Point(counterSize, counterSize),
                    new PIXI.Point(counterSize + 1, counterSize + 1),
                ],
                tweenDuration: 20,
                tweenDelay: 20,
            });
        };

        Tweens.removeTweens(this.counter, "all");
        if (reset) {
            finish(true);
        } else {
            if (this.currentWin / BetManager.Bet <= 1) {
                Tweens.scaleTween(this.counter, {
                    tweenValues: [new PIXI.Point(counterSize, counterSize), new PIXI.Point(counterSize, counterSize)],
                    tweenDuration: 20,
                    tweenDelay: 0,
                    callbackFunction: finish,
                });
            } else {
                Tweens.scaleTween(this.counter, {
                    tweenValues: [
                        new PIXI.Point(counterSize, counterSize),
                        new PIXI.Point(counterSize + 0.2, counterSize + 0.2),
                        new PIXI.Point(counterSize, counterSize),
                    ],
                    tweenDuration: 15,
                    tweenDelay: 20,
                    callbackFunction: finish,
                });
            }
            Tweens.alphaTween(this.counter, {
                alphas: [this.counter.alpha, 1],
                tweenDuration: 5,
                tweenDelay: 0,
            });
        }
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    public reset(showLastSymbols: boolean, spin = false): boolean {
        if (this._mysteryStarted) {
            if (!this._mysteryFinished) {
                this._currentMystery.goToCase("OUTRO_2");
                PlayList.stopAllInGroup("SND_MysteryWin");
                // PlayList.play('SND_MysteryStop');
            } else {
                this._currentMystery.goToOutro();
            }

            this._mysteryFinished = false;
        }
        this._mysteryStarted = false;
        this._currentMystery = null;
        let outroPlayed = false;

        switch (this.timerStatus) {
            case WinAnimatorExtended.ShowFirstWinsStep:
                this.runCounterOutro(true);
                outroPlayed = true;
                break;
            case WinAnimatorExtended.FinishWin:
                this.runCounterOutro(true);
                outroPlayed = true;
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
            case WinAnimatorExtended.EndFirstWinsStep:
                this.currentCounterStep = null;
                this.timerStatus = null;
                this.currentCycle = 0;
                break;
        }

        this._paylines.visible = false;

        this.setActiveElementCaseArray([WinAnimatorExtended.CaseNormal]);
        return super.reset(showLastSymbols, spin);
    }

    //******************************************************************************************************************
    //  ACTIVE ELEMENTS
    //******************************************************************************************************************

    /**
     * Attempts to switch all Active Elements to the earliest case from array the element can be switched to.
     * Elements with no case from the list are ignored.
     * @param s Array of names of cases to try to switch to
     * @param emitEvent
     * @param delay Switch delay
     */
    private setActiveElementCaseArray(s: Array<string>, emitEvent: boolean = false, delay?: number) {
        if (!s.length) {
            return;
        }
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s[0];
        this._activeElements.forEach((element) => {
            for (let i = 0; i < s.length; i++) {
                if (element.hasCase(s[i])) {
                    element.goToCase(s[i], emitEvent, delay);
                    break;
                }
            }
        });
    }

    /**
     * Attempts to switch all Active Elements to the given case if they have this case.
     * Elements without the case are ignored.
     * @param s Case name
     * @param emitEvent
     * @param delay
     */
    private setActiveElementCase(s: string, emitEvent: boolean = false, delay?: number) {
        if (!this.visible) {
            return;
        }
        this._activeElementsCase = s;
        this._activeElements.forEach((element) => {
            if (element.hasCase(s)) {
                element.goToCase(s, emitEvent, delay);
            }
        });
    }

    /**
     * Returns last case switched to, or the first case from a list switched to.
     */
    private getActiveElementCase(): string {
        return this._activeElementsCase;
    }

    protected cycleWins(isFirst: boolean = false) {
        if (
            this.currentWins &&
            !isFirst &&
            this.currentWins.filter((win) => win.type.toString() == "mystery").length > 0
        ) {
            return;
        }

        super.cycleWins(isFirst);
    }
}

export const WinAnimatorExtendedAttributes = {
    ACTIVE_ELEMENTS: "ActiveElements",
    CYCLE_ANIMATION: "CycleAnimation",
    HIGH_WINS_MYSTERY_ANIMATIONS: "HighWinsMysteryAnimations",
    PAYLINE_WIN_DELAY: "PaylineWinDelay",
    PAYLINE_WIN_END_DELAY: "PaylineWinEndDelay",
    PAYLINE_CYCLE_DELAY: "PaylineCycleDelay",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/WinAnimatorExtended.ts
 **/